// Actions do slice `journey`. Somente commit — o fetch vive na classe de API
// (apiCentralConfig), nunca aqui (separação de responsabilidades).
import {
  writeGuide,
  guideScopeFromStore,
} from '@modules/central-config/utils/guide-storage'
import {
  GUIDE_ORIGIN_JOURNEY,
  normalizeGuideOrigin,
} from '@modules/central-config/utils/guide-origin'

// A chave do guia é carimbada por escritório + usuário (ver guide-storage.js).
// `rootGetters` é o único acesso ao usuário logado a partir de uma action
// namespaced — o slice `centralConfig` não guarda identidade.
const escopoDoGuia = (rootGetters) => guideScopeFromStore({ getters: rootGetters })

export const setJourney = ({ commit }, payload) => {
  commit('SET_JOURNEY', payload)
}

export const setJourneyLoading = ({ commit }, loading) => {
  commit('SET_JOURNEY_LOADING', loading)
}

// Nível atual do escritório (spec-004) — recebe o `nivel_atual` cru do payload
// (snake_case); a normalização para camelCase acontece na mutation.
export const setNivelAtual = ({ commit }, payload) => {
  commit('SET_NIVEL_ATUAL', payload)
}

// Actions do slice `guide` (drawer). Somente commit + persistência em localStorage.

// `origin`/`moduleKey` dizem de ONDE o guia foi aberto (ver utils/guide-origin.js).
// São pass-through explícito, e não herdados do estado anterior de propósito:
// uma action que preservasse a origem quando o payload a omite ficaria stateful,
// e um call-site que quer "sessão nova a partir da jornada" herdaria em silêncio
// um 'module' velho.
export const openGuide = (
  { commit, rootGetters },
  { moduleId, taskId, origin, moduleKey } = {}
) => {
  // Abrir uma tarefa sempre expande o painel (collapsed=false).
  const guide = {
    open: true,
    collapsed: false,
    moduleId: moduleId || null,
    taskId: taskId || null,
    origin: normalizeGuideOrigin(origin),
    moduleKey: moduleKey || null,
  }
  commit('SET_GUIDE', guide)
  writeGuide(guide, escopoDoGuia(rootGetters))
}

export const closeGuide = ({ commit, rootGetters }) => {
  const guide = {
    open: false,
    collapsed: false,
    moduleId: null,
    taskId: null,
    origin: GUIDE_ORIGIN_JOURNEY,
    moduleKey: null,
  }
  commit('SET_GUIDE', guide)
  writeGuide(guide, escopoDoGuia(rootGetters))
}

// Recolhe o painel para o FAB (mantém a tarefa ativa; só esconde o painel).
export const collapseGuide = ({ commit, state, rootGetters }) => {
  const guide = { ...state.guide, collapsed: true }
  commit('SET_GUIDE', guide)
  writeGuide(guide, escopoDoGuia(rootGetters))
}

// Reabre o painel a partir do FAB.
export const expandGuide = ({ commit, state, rootGetters }) => {
  const guide = { ...state.guide, collapsed: false }
  commit('SET_GUIDE', guide)
  writeGuide(guide, escopoDoGuia(rootGetters))
}

// Actions da transição "Iniciar etapa" (só commit — a orquestração de tempo
// vive em utils/step-navigation.js `startTaskNavigation`, chamada por
// central-config-module-accordion.vue `startTask()`).

export const setStepTransition = ({ commit }, payload) => {
  commit('SET_STEP_TRANSITION', payload)
}

export const resetStepTransition = ({ commit }) => {
  commit('SET_STEP_TRANSITION', {})
}

// ★ Ponto de integração — marcação automática da etapa ao concluir a ação real
// no destino (ex.: salvar o 1º cliente no Consolidador). Ainda NÃO existe um
// sinal cross-module (evento/GlobalBus) que a tela de destino emita ao
// terminar — implementá-lo tocaria módulos fora do escopo desta mudança
// (proibido: "não alterar a tela de destino"). Quando esse sinal existir, o
// listener chamaria algo como:
//
//   apiCentralConfig.marcarTask({ taskId: guideTaskId, done: true })
//
// (mesma usecase que `central-config-module-accordion.vue`.toggleDoneStatus e
// `central-config-guide-drawer.vue` já usam pra marcar manualmente.)

// Guarda de qual módulo E de qual etapa o usuário saiu, para a home reabrir
// naquele ponto ao voltar (ver `returnToModuleId`/`returnToTaskId` em
// state.js). A etapa entra no payload porque é ela que define o nível a exibir
// num módulo cross-nível. Chamado por `backToJourney` no drawer; consumido e
// limpo pela própria home.
export const setReturnModule = ({ commit }, { moduleId, taskId } = {}) => {
  commit('SET_RETURN_MODULE', { moduleId, taskId })
}

export const clearReturnModule = ({ commit }) => {
  commit('SET_RETURN_MODULE', null)
}

// ---- Tour guiado (jornada guiada) ----
// Actions só commitam — quem chama `apiCentralConfig.marcarTask` e quem navega
// é o componente, pela classe de API. É a mesma divisão que o resto do módulo
// pratica (a action nunca faz fetch).

export const iniciarTour = ({ commit }, payload = {}) => {
  commit('SET_TOUR', {
    ativo: true,
    moduleId: payload.moduleId || null,
    moduleKey: payload.moduleKey || null,
    taskId: payload.taskId || null,
    taskSlug: payload.taskSlug || null,
    passos: payload.passos || [],
    proximaEtapa: payload.proximaEtapa || null,
    indice: 0,
    fase: 'passos',
    recolheuGuia: !!payload.recolheuGuia,
    marcarConcluida: true,
  })
}

export const irParaPassoDoTour = ({ commit }, indice) => {
  commit('SET_TOUR', { indice: Math.max(0, indice || 0) })
}

// Fim dos passos: o balão troca para o desfecho (marcação + oferta da próxima).
export const concluirPassosDoTour = ({ commit }) => {
  commit('SET_TOUR', { fase: 'desfecho' })
}

export const definirMarcacaoDoTour = ({ commit }, marcar) => {
  commit('SET_TOUR', { marcarConcluida: !!marcar })
}

export const encerrarTour = ({ commit }) => {
  commit('SET_TOUR', null)
}
